/**
 * The messages between the app and the backup worker (decision 72; part 24e log, tables T, S, M, N): the app asks for a
 * folder run (each changed book written to the folder it hands over) or a reminder check (which books' changes have gone
 * 7 days without a backup, with the first one's bundle made ready); the worker answers once, at the end. Each side reads
 * what it receives field by field (Definition of Done, security).
 */
import { isId } from '@cockatrice/storage';
import { isDirectory } from './folder.ts';
import type { DirectoryLike, FolderFailure } from './folder.ts';
import { isSha256 } from './log.ts';
import { isLockId } from './lock.ts';

/** T1–T7: write each changed book to `folder`, made by `device`; a book with no title is named `untitled`. */
export interface RunJob {
  readonly kind: 'run';
  readonly folder: DirectoryLike;
  readonly device: string;
  readonly untitled: string;
}

/** M5, M6, M10: the books due a reminder, and the bundle of the first (book `prefer` first when it is due). */
export interface CheckJob {
  readonly kind: 'check';
  readonly device: string;
  readonly untitled: string;
  readonly prefer: string | null;
}

/** A job. */
export type BackupJob = RunJob | CheckJob;

/** M6: a book's bundle made ready for the reminder's press: its file, the name to save it under, digest and time. */
export interface ReadyBundle {
  readonly bookId: string;
  readonly title: string;
  readonly file: Blob;
  readonly name: string;
  readonly sha256: string;
  readonly at: number;
  /** Part 41b, deep-fix r1 (C2–C4, C7): the lock the file is sealed under (its ID), null for a plain one. */
  readonly lock: string | null;
}

/**
 * What the worker answers. Part 41b: `unlocked` names the books whose locked file didn't open back (BP3, E3), so none was
 * written or made ready for them.
 */
export type BackupReply =
  /** S1–S8: the run ended; the books whose bundle couldn't be made (N6), and those whose locked file didn't open back (E3). */
  | { readonly kind: 'ran'; readonly written: number; readonly failed: readonly string[]; readonly unlocked: readonly string[] }
  /** W6, K5: the run stopped on the folder (N2–N5). */
  | { readonly kind: 'stopped'; readonly reason: FolderFailure; readonly failed: readonly string[]; readonly unlocked: readonly string[] }
  /** M3, M6: the books due, the first one's bundle (sealed when locked, E8), and the books whose bundle couldn't be made or locked. */
  | { readonly kind: 'checked'; readonly due: readonly string[]; readonly ready: ReadyBundle | null; readonly failed: readonly string[]; readonly unlocked: readonly string[] }
  /** E7: the backup lock is stored but can't be read: nothing was made or written (never plaintext in its place). */
  | { readonly kind: 'lock-unreadable' }
  /** Nothing could start: the store was upgraded by a newer build, or anything else. */
  | { readonly kind: 'failed'; readonly reason: 'outdated' | 'failed' };

const FOLDER_FAILURES: readonly FolderFailure[] = ['permission', 'gone', 'full', 'failed'];

/** The own fields of a plain object with exactly `keys`, or null. */
function fieldsOf(value: unknown, keys: readonly string[]): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === keys.length && keys.every((key) => fields.has(key)) ? fields : null;
}

/** Whether `value` is a whole number from 0. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** A list of book IDs, or null. */
function readIds(value: unknown): readonly string[] | null {
  return Array.isArray(value) && value.every((each) => isId(each)) ? value.map(String) : null;
}

/** The job a message asks for, or null for anything else. */
export function readJob(value: unknown): BackupJob | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'run') {
    const fields = fieldsOf(value, ['kind', 'folder', 'device', 'untitled']);
    const folder = fields?.get('folder');
    const device = fields?.get('device');
    const untitled = fields?.get('untitled');
    return isDirectory(folder) && isId(device) && typeof untitled === 'string' ? { kind, folder, device, untitled } : null;
  }
  if (kind === 'check') {
    const fields = fieldsOf(value, ['kind', 'device', 'untitled', 'prefer']);
    const device = fields?.get('device');
    const untitled = fields?.get('untitled');
    const prefer = fields?.get('prefer');
    return isId(device) && typeof untitled === 'string' && (prefer === null || isId(prefer)) ? { kind, device, untitled, prefer } : null;
  }
  return null;
}

function readReady(value: unknown): ReadyBundle | null | undefined {
  if (value === null) return null;
  const fields = fieldsOf(value, ['bookId', 'title', 'file', 'name', 'sha256', 'at', 'lock']);
  const bookId = fields?.get('bookId');
  const title = fields?.get('title');
  const file = fields?.get('file');
  const name = fields?.get('name');
  const sha256 = fields?.get('sha256');
  const at = fields?.get('at');
  const lock = fields?.get('lock');
  return isId(bookId) && typeof title === 'string' && file instanceof Blob && typeof name === 'string' && name !== '' && isSha256(sha256) && isCount(at) && (lock === null || isLockId(lock))
    ? { bookId, title, file, name, sha256, at, lock }
    : undefined;
}

/** The reply a message is, or null for anything else. */
export function readReply(value: unknown): BackupReply | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'ran') {
    const fields = fieldsOf(value, ['kind', 'written', 'failed', 'unlocked']);
    const written = fields?.get('written');
    const failed = readIds(fields?.get('failed'));
    const unlocked = readIds(fields?.get('unlocked'));
    return isCount(written) && failed !== null && unlocked !== null ? { kind, written, failed, unlocked } : null;
  }
  if (kind === 'stopped') {
    const fields = fieldsOf(value, ['kind', 'reason', 'failed', 'unlocked']);
    const reason = FOLDER_FAILURES.find((known) => known === fields?.get('reason'));
    const failed = readIds(fields?.get('failed'));
    const unlocked = readIds(fields?.get('unlocked'));
    return reason !== undefined && failed !== null && unlocked !== null ? { kind, reason, failed, unlocked } : null;
  }
  if (kind === 'checked') {
    const fields = fieldsOf(value, ['kind', 'due', 'ready', 'failed', 'unlocked']);
    const due = readIds(fields?.get('due'));
    const ready = readReady(fields?.get('ready'));
    const failed = readIds(fields?.get('failed'));
    const unlocked = readIds(fields?.get('unlocked'));
    return due !== null && ready !== undefined && failed !== null && unlocked !== null ? { kind, due, ready, failed, unlocked } : null;
  }
  if (kind === 'lock-unreadable') return fieldsOf(value, ['kind']) === null ? null : { kind };
  if (kind === 'failed') {
    const fields = fieldsOf(value, ['kind', 'reason']);
    const reason = fields?.get('reason');
    return reason === 'outdated' || reason === 'failed' ? { kind, reason } : null;
  }
  return null;
}
